import { domainCopy } from '../copy';
import type { Language } from '../types/language';
import type { AgeBand } from './child';
import type { ID, ISODate, ISODateTime } from './ids';

/**
 * **VILLAGE SQUARE** (P35, P35b, P35c, P35d; notes on 1c, P23 and P18; Evan, 2026-09-27). Guides, printables, events,
 * our own products, help with the grown-ups' own money, and partner offers — for grown-ups, never on a child's screen or
 * a child's device, never in a push. The team writes and publishes every item in the admin with the same rules as
 * lessons: a draft, a review bound to the words, a second person to put it live, an immutable version, a take-down.
 */

/** The shelves, in the order P35 draws them. A shelf with nothing on it does not come back at all. */
export const VILLAGE_KINDS = [
  'guide',
  'printable',
  'event',
  'ourProduct',
  'money',
  'partnerOffer',
] as const;
export type VillageKind = (typeof VILLAGE_KINDS)[number];

/** P35d and the Monday email draw from these; a partner offer never goes on Home or in an email. */
export const VILLAGE_CARD_KINDS: readonly VillageKind[] = [
  'guide',
  'printable',
  'event',
  'ourProduct',
];
/** The Monday email's one item (P18): a guide or an event. */
export const VILLAGE_EMAIL_KINDS: readonly VillageKind[] = ['guide', 'event'];

/**
 * **Who an item is for**, matched to the family's children on the server: an age range, and optionally the reading
 * levels it suits. An item with no audience is for everyone. Nothing about the family leaves the server.
 */
export interface VillageAudience {
  minAge: number;
  maxAge: number;
  /** Absent: any reading level. */
  readingLevels?: AgeBand[];
}

/** A guide's text (P35b): paragraphs and subheads, read in the app, never leaving it. */
export interface VillageGuideBlock {
  type: 'paragraph' | 'heading';
  text: string;
}

/** The printables the API holds (P35c), US Letter and A4 — the three first drawings. */
export const VILLAGE_PRINTABLE_FILES = [
  'chore-chart.png',
  'savings-goal-tracker.png',
  'bamboo-bucks-award.png',
] as const;
export type VillagePrintableFile = (typeof VILLAGE_PRINTABLE_FILES)[number];

/**
 * **THE ART'S VERSION, AND THE URL CARRIES IT** (Evan, 9 Oct: *"give the fixed file a new name or a version in
 * its URL so no cache serves the old one"*).
 *
 * The savings-goal tracker was redrawn on 6 Oct and its served copy replaced (`c08eaeb2`) — and families kept
 * seeing the overflowing one. **The server was never wrong.** A fetch of exactly what the app fetches, on
 * 10 Oct, returned bytes identical to the repo's. What was wrong is that the URL never changed while the
 * answer carried `cache-control: public, max-age=31536000, immutable` — and `immutable` means *do not
 * revalidate, ever*. Every device that had opened the printable before 6 Oct was holding the old drawing for
 * a year, and nothing short of deleting the app would have shifted it.
 *
 * **`immutable` is the right header; an unversioned URL is the wrong partner for it.** So the version goes in
 * the URL: new art, new number, new cache key, and the old answer is simply never asked for again.
 *
 * **Bump the number in the same commit that replaces the art.** A redraw that forgets this ships to nobody.
 */
export const VILLAGE_PRINTABLE_VERSION: Record<VillagePrintableFile, number> = {
  'chore-chart.png': 1,
  /** 2 — redrawn 6 Oct (`c08eaeb2`): the sixth column overflowed the card. */
  'savings-goal-tracker.png': 2,
  'bamboo-bucks-award.png': 1,
};

/**
 * Where a printable's picture is served (public, like a panda's: an image tag carries no bearer), with the
 * art's version on it.
 *
 * The query string costs the API nothing — static serving routes on the path, and a fetch of
 * `…savings-goal-tracker.png?v=2` against production answers 200 with the same bytes. There is no CDN in
 * front of it to strip the query (no `via`, no `x-cache`, no `age` on the answer).
 */
export const villagePrintableUrl = (file: VillagePrintableFile): string =>
  // A fallback rather than an index: this is the shape that crashed P28 on build 21, and a printable whose
  // name reached us from a database is not something the compiler has actually checked.
  `/v1/village-square/printables/${file}?v=${VILLAGE_PRINTABLE_VERSION[file] ?? 1}`;

/** An item's words and settings, as the team writes them — the draft, and each published version. */
export interface VillageContent {
  kind: VillageKind;
  title: string;
  /** The one line under the title (P35c "A week of chores to tick off on the fridge.", P35's "Credit counseling…"). */
  line?: string;
  /** Whose it is, drawn as the card's name ("NFCC", "Kredit Academy"). A partner's card takes the partner's name. */
  provider?: string;
  audience?: VillageAudience;
  /**
   * **The row's picture** (P35, 28 Sep: "a mission picture from the icon library, picked by the team in the admin"): an
   * icon key — a bundled glyph, or an uploaded icon that is live. A guide or printable only. The app draws it as it draws
   * a mission's picture (the icon manifest); absent, the row is drawn without one.
   */
  picture?: string;
  /** A guide: minutes to read ("3 min read"), and its text. A printable may say how long it takes too. */
  readMinutes?: number;
  body?: VillageGuideBlock[];
  /** A printable: which drawing. */
  printable?: VillagePrintableFile;
  /** An event: when it starts; members-only workshops are marked Family Plus and shown only to Family Plus families. */
  event?: { startsAt: ISODateTime; familyPlusOnly?: boolean };
  /**
   * Where the card's button goes (Register, Get the app, Book, the partner's site) and its label. A partner's URL may
   * hold `{code}`, which the server fills with that partner's one referral code — never anything about a family.
   */
  link?: { url: string; label: string };
  /** A partner's card (Help with your money, Partner offers): only live while the partner is approved and checked. */
  partnerId?: ID;
}

/** The fee line P35 draws under the Partner tag, only when that partner actually pays one. */
export const VILLAGE_FEE_LINE = domainCopy().village.feeLine;
/** The tag every partner card carries, left of the partner's name. */
export const VILLAGE_PARTNER_TAG = domainCopy().village.partnerTag;
/** After a Was this helpful? tap (P35b). */
export const VILLAGE_HELPFUL_THANKS = domainCopy().village.helpfulThanks;
/** The fee line, the partner tag and the thanks, in a language (the constants above are the English). */
export const villageLines = (language?: Language) => {
  const v = domainCopy(language).village;
  return { feeLine: v.feeLine, partnerTag: v.partnerTag, helpfulThanks: v.helpfulThanks };
};

/** A card on a shelf, on Home (P35d) or in the Monday email: what the card draws. */
export interface VillageCard {
  id: ID;
  kind: VillageKind;
  title: string;
  line?: string;
  provider?: string;
  readMinutes?: number;
  /** "For ages 6 to 9" (P35b's eyebrow). */
  ages?: { min: number; max: number };
  event?: { startsAt: ISODateTime; familyPlusOnly?: boolean };
  /**
   * Present on every partner card: the Partner tag, whether the fee line shows, and the partner's logo — only once
   * Evan approved that file; without it, draw the name in text.
   */
  partner?: { name: string; paysFee: boolean; logo?: PartnerLogo };
  /** A printable's picture (public, like a panda's). */
  imageUrl?: string;
  /** The row's picture: an icon key from the library (`VillageContent.picture`); absent, none. */
  picture?: string;
}

/** One item opened (P35b, P35c, or a card's detail): the card plus its words, its button and any disclosures. */
export interface VillageItem extends VillageCard {
  body?: VillageGuideBlock[];
  link?: { url: string; label: string };
  /** A partner's required disclosures (Edward Jones: not FDIC insured, may lose value). */
  disclosures?: string[];
}

/** P35: the shelves with something on them, in order. */
export interface VillageSquare {
  shelves: Array<{ kind: VillageKind; items: VillageCard[] }>;
}

/**
 * **This week's pick, its own read** (P35's card over the header; Evan's ruling, 28 Sep: "its own read, not the Home
 * card's"): the same card P35d draws, kind and all. Closing the Home card hides only that card — P35 shows the pick all
 * week regardless. `pick: null` only when nothing is live to show.
 */
export interface VillagePick {
  pick: VillageCard | null;
}

/**
 * P35d: this week's pick on Home, or `card: null` — when this grown-up closed it this week (it comes back with next
 * week's pick), or nothing is live to show.
 */
export interface VillageHomeCard {
  card: VillageCard | null;
}

/**
 * **What can be the weekly pick** (P35d, 28 Sep): a guide or a printable, never a partner's card. The team lines one up
 * per week in the admin, the same for every family; each starts Monday morning in the family's time, with the report.
 */
export const VILLAGE_PICK_KINDS: readonly VillageKind[] = ['guide', 'printable'];

/** One week in the admin's line-up: what the team picked, and what families get that week. */
export interface VillageWeekPick {
  /** The Monday the week starts (families' own Monday, in their time zone). */
  week: ISODate;
  /** What the team lined up, or null. */
  itemId: ID | null;
  /**
   * What families get that week, as things stand now: the pick while it is live, else the newest guide (Evan: "If
   * nothing is lined up, or the pick is unpublished, the newest guide is served instead"). Null when nothing is live.
   */
  served: { id: ID; title: string; kind: VillageKind; fallback: boolean } | null;
  setBy?: ID;
  setAt?: ISODateTime;
}

// ---- the admin (the team's side) ---------------------------------------------------------------------------------------

export type VillageItemStatus = 'draft' | 'live' | 'takenDown';

/** A sign-off bound to the words it approved: it counts only while `current` (the draft unchanged since). */
export interface VillageSignOff {
  by: ID;
  byName?: string;
  at: ISODateTime;
  current: boolean;
}

/** The totals the team sees; there is nothing per person to see. */
export interface VillageCounts {
  taps: number;
  helpfulYes: number;
  helpfulNo: number;
}

export interface VillageAdminItem {
  id: ID;
  status: VillageItemStatus;
  /** The draft, when there is one — what review and publish act on. */
  draft?: VillageContent;
  /** What families see, when live. */
  live?: VillageContent;
  liveVersion?: number;
  review?: VillageSignOff;
  /** A partner's card: its compliance team's approval of the wording, recorded by an owner, bound to the words. */
  partnerApproval?: VillageSignOff;
  draftAuthors: ID[];
  counts: VillageCounts;
  updatedAt: ISODateTime;
}

export interface VillageItemVersion {
  version: number;
  content: VillageContent;
  publishedAt: ISODateTime;
  publishedBy: ID;
  reviewedBy?: ID;
}

/**
 * **A partner** (P35's notes): an owner approves it; counsel checks the wording once before its first card goes live;
 * its one referral code is the partner's, never a family's; the fee line shows only when it pays. Listing needs FDIC
 * or NCUA insured accounts, clear pricing with no teaser rates, no marketing to children, and no selling a family's
 * data — the owner attests to all four by approving.
 */
export interface VillagePartner {
  id: ID;
  name: string;
  referralCode: string;
  paysFee: boolean;
  disclosures: string[];
  counselChecked: boolean;
  approvedBy?: ID;
  approvedAt?: ISODateTime;
  createdAt: ISODateTime;
  /** Its logo, when one was uploaded, and whether Evan approved it. */
  logo?: PartnerLogoAdmin;
}

/**
 * **A partner's logo** (Evan, 2026-09-28): served like the pandas — each file a new version at permanent addresses,
 * cached for good — at 28, 56 and 84 pixels tall (a 28-point logo at 1×, 2× and 3×), its width following the file's.
 */
export const PARTNER_LOGO_HEIGHTS = [28, 56, 84] as const;
export interface PartnerLogoImage {
  height: number;
  width: number;
  /** Under the API's address, like a panda's. */
  url: string;
}
export interface PartnerLogo {
  version: string;
  images: PartnerLogoImage[];
}
/** The admin's view: the file as uploaded, and whether Evan approved this version (families see only an approved one). */
export interface PartnerLogoAdmin extends PartnerLogo {
  approved: boolean;
  approvedAt?: ISODateTime;
}
export const partnerLogoPath = (partnerId: ID, version: string, height: number): string =>
  `/v1/village-square/partner-logos/${partnerId}/${version}/${height}.png`;

/** An upload: PNG or SVG, as base64, 2 MB at most. */
export const PARTNER_LOGO_TYPES = ['image/png', 'image/svg+xml'] as const;
export const PARTNER_LOGO_MAX_BYTES = 2 * 1024 * 1024;
export interface PartnerLogoUpload {
  contentType: (typeof PARTNER_LOGO_TYPES)[number];
  /** The file, base64. */
  data: string;
}

/** What is wrong with an upload before it is decoded — empty when it may be one. The API then checks the image itself. */
export function partnerLogoProblem(
  u: { contentType?: unknown; data?: unknown },
  language?: Language,
): string | null {
  const t = domainCopy(language).village.logo;
  if (!(PARTNER_LOGO_TYPES as readonly unknown[]).includes(u.contentType)) return t.type;
  if (typeof u.data !== 'string' || !u.data) return t.file;
  if (Math.floor((u.data.replace(/=+$/, '').length * 3) / 4) > PARTNER_LOGO_MAX_BYTES) return t.size;
  // the files' own first bytes, as base64: a PNG's signature; an SVG's "<svg" or "<?xml"
  if (u.contentType === 'image/png' && !u.data.startsWith('iVBORw0KGgo')) return t.png;
  if (u.contentType === 'image/svg+xml' && !/^(PHN2Zy|PD94bWw|77u\/)/.test(u.data)) return t.svg;
  return null;
}

export interface VillagePartnerInput {
  name: string;
  referralCode: string;
  paysFee?: boolean;
  disclosures?: string[];
  counselChecked?: boolean;
}
